import { apiFactory, headers } from '~/src/dash/api/api-factory';
import { transformCampaignWrapper } from '~/src/dash/api/campaign/campaign';
import {
  transformChannelGroupLabelFromAPI,
  transformChannelGroupLabelToAPI,
} from '~/src/dash/api/channel-group-label';
import {
  fromAPIHeaderBidderWrapper,
  IChannelGroup,
  ISitesInfo,
  retryFunc,
  transformSiteWrapper,
} from '~/src/dash/api/console-api';
import {
  parseArray,
  parseNumber,
  parseString,
  transformModel,
} from '~/src/dash/api/resource-transforms';
import { createUrl, restPatch, restPost } from '~/src/dash/api/rest';
import { Auth } from '~/src/dash/api/transform-auth';

export const toAPIChannelGroup = transformModel<IChannelGroup>({
  ID: parseString(['ID', 'id']),
  CreatedAt: 'CreatedAt',
  UpdatedAt: 'UpdatedAt',
  DeletedAt: 'DeletedAt',
  PublisherID: parseNumber(['PublisherID', 'publisher_id']),
  Name: parseString(['Name', 'name']),
  ChannelIDs: parseString(['ChannelIDs', 'channel_ids']),
  DefaultWeight: parseNumber('DefaultWeight'),
  ChannelGroupLabels: parseArray(['ChannelGroupLabels'], transformChannelGroupLabelToAPI),
});

export const transformSitesInfo = transformModel<ISitesInfo>({
  ID: parseNumber(['ID']),
  Name: parseString(['Name']),
  BidFloor: parseNumber(['BidFloor']),
});

export const transformChannelGroup = transformModel<IChannelGroup>({
  ID: parseNumber(['ID', 'id']),
  CreatedAt: 'CreatedAt',
  UpdatedAt: 'UpdatedAt',
  DeletedAt: 'DeletedAt',

  PublisherID: parseString(['PublisherID', 'publisher_id']),
  Name: parseString(['Name', 'name']),
  ChannelIDs: parseString(['ChannelIDs', 'channel_ids']),
  Sites: parseArray(['Sites', 'sites'], transformSiteWrapper),
  SitesInfo: parseArray(['SitesInfo'], transformSitesInfo),
  DefaultWeight: parseNumber('DefaultWeight'),
  ChannelGroupLabels: parseArray(['ChannelGroupLabels'], transformChannelGroupLabelFromAPI),
  HeaderBidders: parseArray(['HeaderBidders'], fromAPIHeaderBidderWrapper),
  Campaigns: parseArray(['AdserverCampaigns'], transformCampaignWrapper),
});

export function transformChannelGroupWrapper(fromObj: any): IChannelGroup {
  return transformChannelGroup(fromObj);
}

export const getChannelGroups = apiFactory({
  method: 'GET',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'channel_groups',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    const channelGroups = (json.ChannelGroups || []).map(transformChannelGroup);
    return {
      channelGroups,
    };
  },
});

export async function updateChannelGroup(
  channelGroup: IChannelGroup,
): Promise<[IChannelGroup?, Error?]> {
  const reqUrl = createUrl('/channel_group', channelGroup.PublisherID.toString());
  const parsedCG = toAPIChannelGroup(channelGroup);

  try {
    const ret = (await restPatch<IChannelGroup>(reqUrl, parsedCG)).parsedBody;
    return [transformChannelGroup(ret), undefined];
  } catch (e) {
    console.error(e);
    return [undefined, e];
  }
}

export async function createChannelGroup(
  channelGroup: IChannelGroup,
): Promise<[IChannelGroup?, Error?]> {
  const reqUrl = createUrl('/channel_group', channelGroup.PublisherID.toString());
  const parsedCG = toAPIChannelGroup(channelGroup);
  // delete ID field since ChannelGroup is new
  // @ts-ignore
  delete parsedCG.ID;
  try {
    const ret = (await restPost<IChannelGroup>(reqUrl, parsedCG)).parsedBody;
    return [transformChannelGroup(ret), undefined];
  } catch (e) {
    console.error(e);
    return [undefined, e];
  }
}

export const deleteChannelGroup = apiFactory({
  method: 'DELETE',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'channel_group',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
});

// the following two objects are created to prevent misspellings and reference only viable options
// please import these objects if you need to fill in preloads, uiFields

// this mimics all possible preloads as listed in the channel groups struct
// use to fill in preloads
export const channelGroupPreloads = {
  ADSERVERCAMPAIGNS: 'AdserverCampaigns',
  BRANDSAFETYTRACKERS: 'BrandSafetyTrackers',
  CHANNELGROUPLABELS: 'ChannelGroupLabels',
  DEALS: 'Deals',
  HEADERBIDDERS: 'HeaderBidders',
  SITES: 'Sites',
};

export interface IGetChannelGroupParams {
  publisherID: string;
  channelGroupIDs?: number[];
  preloads?: string[];
  uiFields?: string[];
}

// this function allows us to set defaults automatically when not passing in certain params
// ex: getChannelGroupsPromise({ publisherId: 7, preloads: [channelGroupPreloads.SITES] })
// above will create these params ?publisher_id=7&preloads=Sites&ui_fields=
export const getChannelGroupsPromise = ({
  publisherID,
  channelGroupIDs = [],
  preloads = [], // default sends an empty array; no preloads
  uiFields = [], // default sends an empty array; returns all direct struct key-value pairs
}: IGetChannelGroupParams) =>
  getChannelGroups({
    query: {
      publisher_id: publisherID,
      channel_group_ids: channelGroupIDs.join(','),
      preloads: preloads.join(','),
      ui_fields: uiFields.join(','),
    },
  });
